import {
  ContactSchema,
  type PersonContactWithAddressEmergencyAccess,
  type PersonGeneral,
  personGeneralSchema,
  personMinimalGeneralFieldSchema,
} from '@quromedical/models';
import { CrudForm, type SubmissionResult } from 'components/form';
import {
  type ContactWithOutCoordinates,
  minimalPersonGeneralFields,
  personGeneralFields,
  resolveFormContactFields,
} from 'core/forms';
import type { Fetcher } from 'design-system/components';
import { useAddressAutoFill } from 'hooks/useAddressAutoFill';
import type React from 'react';
import { useState } from 'react';
import { strings } from 'strings';

interface GeneralFormProps {
  data?: Partial<PersonGeneral>;
  minimalData?: boolean;
  handleSubmit: (data: PersonGeneral) => Promise<SubmissionResult<unknown>>;
  handleCancel: () => void;
}

interface ContactFormProps {
  data?: Partial<PersonContactWithAddressEmergencyAccess>;
  hasEmergencyAccess?: boolean;
  handleSubmit: (
    data: PersonContactWithAddressEmergencyAccess,
  ) => Promise<SubmissionResult<unknown>>;
  handleCancel: () => void;
  addressFetcher?: Fetcher<string>;
  getAddressPartial?: (
    placeId: string,
  ) => Promise<Partial<PersonContactWithAddressEmergencyAccess>>;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  data = {},
  hasEmergencyAccess = false,
  handleCancel,
  handleSubmit,
  getAddressPartial,
  addressFetcher,
}) => {
  const [formState, setFormState] =
    useState<Partial<PersonContactWithAddressEmergencyAccess>>(data);
  const contactFields = resolveFormContactFields(
    formState,
    hasEmergencyAccess,
    addressFetcher,
  );

  const { onContactChange, placeId } = useAddressAutoFill(
    setFormState,
    getAddressPartial,
  );

  return (
    <CrudForm<ContactWithOutCoordinates>
      key={placeId}
      title={strings.FormLabelPersonContactDetails}
      validationSchema={ContactSchema}
      fields={contactFields}
      initialValues={formState}
      onSubmit={handleSubmit}
      onSecondarySubmit={handleCancel}
      showSecondarySubmitButton
      showSubmitButton
      onChangeDebounceTime={0}
      onChange={onContactChange}
    />
  );
};

export const GeneralForm: React.FC<GeneralFormProps> = ({
  data = {},
  minimalData = false,
  handleCancel,
  handleSubmit,
}) => {
  const fields = minimalData ? minimalPersonGeneralFields : personGeneralFields;
  const schema = minimalData
    ? personMinimalGeneralFieldSchema
    : personGeneralSchema;

  return (
    <CrudForm
      title={strings.FormLabelPersonDetails}
      validationSchema={schema}
      fields={fields}
      initialValues={data}
      onSubmit={handleSubmit}
      onSecondarySubmit={handleCancel}
      showSecondarySubmitButton
      showSubmitButton
    />
  );
};
